import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';

import { MdwAside } from '@spaces-ui/meadow/components/aside';
import { MdwListItem } from '@spaces-ui/meadow/components/list-item';
import { ThemeIntrospectionService, ThemeService } from '@spaces-ui/theme';

import { buildPaletteIndex, deriveTokenLabel, toVarMap } from './token-label.util';
import { TokenGroups, TokenService } from './tokens-service';

/** One value column: a concrete theme class id and its display name. */
interface ThemeColumnVm {
  id: string;
  name: string;
}
interface TokenRowVm {
  token: string;
  cssVar: string;
  /** Precomputed `var(--x)` reference the swatch binds to. */
  varRef: string;
  /** `null` = token missing from the theme's composed set (N/D badge). */
  lightLabel: string | null;
  darkLabel: string | null;
}
interface TokenCategoryVm {
  category: string;
  tokens: TokenRowVm[];
}
interface TokenOverviewVm {
  light: ThemeColumnVm;
  /** `null` = the selected family has no dark variant; column hidden. */
  dark: ThemeColumnVm | null;
  categories: TokenCategoryVm[];
}

@Component({
  selector: 'app-tokens',
  imports: [MdwAside, MdwListItem],
  templateUrl: './token-overview.html',
  styleUrl: './token-overview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TokenOverview implements OnInit {
  private tokenService = inject(TokenService);
  private themeService = inject(ThemeService);
  private introspection = inject(ThemeIntrospectionService);

  public isCollapsed = signal(false);

  public readonly customThemeActive = this.themeService.customThemeActive;

  private readonly tokenGroups = signal<TokenGroups | null>(null);

  /**
   * The full page view model for the currently-selected theme family. Token
   * values themselves are resolved by the CSS cascade (each cell is scoped by
   * its column's theme class and the swatch paints `var(--token)`); only the
   * labels are derived here, from the theme's composed base + delta variables.
   *
   * Recomputes when the family changes and when the introspection signals bump
   * on window `load`, so a pre-parse stylesheet snapshot heals itself.
   */
  public readonly vm = computed<TokenOverviewVm | null>(() => {
    const groups = this.tokenGroups();
    if (groups === null) {
      return null;
    }

    const family = this.themeService.family();
    const themes = this.introspection.themes();
    const lightInfo = themes.find(theme => theme.family === family && theme.mode === 'light');
    const darkInfo = themes.find(theme => theme.family === family && theme.mode === 'dark');

    // Before the global stylesheet is parsed, discovery is empty — fall back to
    // the neutral pair so the table still renders; the computed heals on load.
    const light: ThemeColumnVm = lightInfo
      ? { id: lightInfo.id, name: `${lightInfo.name} — Light` }
      : { id: 'sunshine', name: 'Light' };
    const dark: ThemeColumnVm | null = darkInfo
      ? { id: darkInfo.id, name: `${darkInfo.name} — Dark` }
      : lightInfo
        ? null
        : { id: 'moonshine', name: 'Dark' };

    const paletteIndex = buildPaletteIndex(this.introspection.palette());
    const lightVars = toVarMap(this.introspection.getThemeVariables(light.id));
    const darkVars = dark ? toVarMap(this.introspection.getThemeVariables(dark.id)) : null;

    const categories = Object.entries(groups).map(([category, tokens]) => ({
      category,
      tokens: tokens.map(({ token, cssVar }) => ({
        token,
        cssVar,
        varRef: `var(${cssVar})`,
        lightLabel: deriveTokenLabel(cssVar, lightVars, paletteIndex),
        darkLabel: darkVars ? deriveTokenLabel(cssVar, darkVars, paletteIndex) : null,
      })),
    }));

    return { light, dark, categories };
  });

  async ngOnInit(): Promise<void> {
    this.tokenGroups.set(await this.tokenService.getTokens());
  }
}
